// imageProcessor.ts - Updated to use cloud function proxy for CORS bypass

import { Functions, httpsCallable } from 'firebase/functions';

export interface ImageSizes {
  favicon16: string;
  favicon32: string;
  favicon192: string;
  favicon512: string;
  appleTouchIcon: string;
  socialMediaImage: string;
}

export class ImageProcessor {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private functions?: Functions;

  constructor(functions?: Functions) {
    this.canvas = document.createElement('canvas');
    const context = this.canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas 2D context not supported');
    }
    this.ctx = context;
    this.functions = functions;
    
    // Set canvas quality settings
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';
  }

  // Validate uploaded image
  validateImage(file: File): { valid: boolean; error?: string } {
    console.log('🔍 Validating image file:', file.name);
    
    // Check file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      const error = 'File size must be less than 5MB';
      console.error('❌ File too large:', file.size, 'bytes');
      return { valid: false, error };
    }

    // Check file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      const error = 'Only PNG, JPEG, JPG, and WebP files are allowed';
      console.error('❌ Invalid file type:', file.type);
      return { valid: false, error };
    }

    console.log('✅ Image validation passed');
    return { valid: true };
  }

  // Load image from file
  async loadImage(file: File): Promise<HTMLImageElement> {
    console.log('📖 Loading image from file:', file.name);
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        console.log(`✅ Image loaded: ${img.width}x${img.height}`);
        
        // Validate image dimensions
        if (img.width === 0 || img.height === 0) {
          reject(new Error('Invalid image dimensions'));
          return;
        }
        
        // Log if image is not square (warning, but not error)
        if (img.width !== img.height) {
          console.warn('⚠️ Image is not square, will be cropped to fit');
        }
        
        resolve(img);
      };
      
      img.onerror = (error) => {
        console.error('❌ Failed to load image:', error);
        reject(new Error('Failed to load image'));
      };
      
      // Create object URL for the image
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
      
      // Clean up object URL after loading
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        console.log(`✅ Image loaded: ${img.width}x${img.height}`);
        resolve(img);
      };
    });
  }

  // Load image from URL using cloud function proxy (CORS bypass)
  async loadImageFromUrl(imageUrl: string, appId: string): Promise<HTMLImageElement> {
    console.log('📖 Loading image from URL via cloud function proxy:', imageUrl);
    
    if (!this.functions) {
      throw new Error('Firebase Functions not initialized. Cannot use cloud function proxy.');
    }

    try {
      // Use cloud function to fetch the image
      const fetchImageFunc = httpsCallable(this.functions, 'fetchImageForProcessing');
      
      console.log('🔄 Calling cloud function to fetch image...');
      const result = await fetchImageFunc({ imageUrl, appId });
      
      // @ts-ignore
      if (!result.data?.success) {
        // @ts-ignore
        throw new Error(result.data?.message || 'Cloud function failed to fetch image');
      }

      // @ts-ignore
      const { imageData, method, size } = result.data;
      console.log(`✅ Image fetched via cloud function (${method}): ${size} bytes`);

      // Load the base64 image data into an HTMLImageElement
      return new Promise((resolve, reject) => {
        const img = new Image();
        
        img.onload = () => {
          console.log(`✅ Image loaded from cloud function data: ${img.width}x${img.height}`);
          
          if (img.width === 0 || img.height === 0) {
            reject(new Error('Invalid image dimensions'));
            return;
          }
          
          resolve(img);
        };
        
        img.onerror = (error) => {
          console.error('❌ Failed to load image from cloud function data:', error);
          reject(new Error('Failed to load image from cloud function data'));
        };
        
        img.src = imageData;
      });

    } catch (error) {
      console.error('❌ Cloud function proxy failed:', error);
      
      // Provide helpful error messages
      if (error instanceof Error) {
        if (error.message.includes('unauthenticated')) {
          throw new Error('Authentication required. Please sign in to generate images.');
        } else if (error.message.includes('permission-denied')) {
          throw new Error('Permission denied. You do not have access to this app.');
        } else if (error.message.includes('not-found')) {
          throw new Error('App not found or image URL is invalid.');
        }
      }
      
      throw new Error(`Failed to load image via cloud function: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Fallback: Direct load with CORS (for backwards compatibility)
  async loadImageDirectly(imageUrl: string): Promise<HTMLImageElement> {
    console.log('📖 Attempting direct image load (may fail due to CORS):', imageUrl);
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      // Try with crossOrigin for CORS
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        if (img.width === 0 || img.height === 0) {
          reject(new Error('Invalid image dimensions'));
          return;
        }
        console.log(`✅ Direct image load succeeded: ${img.width}x${img.height}`);
        resolve(img);
      };
      
      img.onerror = (error) => {
        console.error('❌ Direct image load failed (likely CORS):', error);
        reject(new Error('Direct image load failed due to CORS restrictions'));
      };
      
      img.src = imageUrl;
    });
  }

  // Resize image to specific dimensions with better quality
  resizeImage(img: HTMLImageElement, width: number, height: number, description: string = ''): string {
    console.log(`🔄 Resizing image to ${width}x${height}${description ? ` (${description})` : ''}`);
    
    try {
      this.canvas.width = width;
      this.canvas.height = height;

      // Clear canvas with transparent background
      this.ctx.clearRect(0, 0, width, height);

      // Calculate dimensions to maintain aspect ratio and center the image
      const imgAspect = img.width / img.height;
      const canvasAspect = width / height;

      let drawWidth, drawHeight, offsetX, offsetY;

      if (imgAspect > canvasAspect) {
        // Image is wider than canvas aspect ratio - fit height, crop width
        drawHeight = height;
        drawWidth = height * imgAspect;
        offsetX = (width - drawWidth) / 2;
        offsetY = 0;
      } else {
        // Image is taller than canvas aspect ratio - fit width, crop height
        drawWidth = width;
        drawHeight = width / imgAspect;
        offsetX = 0;
        offsetY = (height - drawHeight) / 2;
      }

      // Use high-quality scaling
      this.ctx.imageSmoothingEnabled = true;
      this.ctx.imageSmoothingQuality = 'high';

      // Draw image centered and covering the entire canvas
      this.ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      // Convert to data URL with high quality
      const dataURL = this.canvas.toDataURL('image/png', 1.0);
      
      // Validate the generated data URL
      if (!dataURL || !dataURL.startsWith('data:image/')) {
        throw new Error(`Failed to generate valid data URL for ${description}`);
      }
      
      console.log(`✅ Generated ${width}x${height} image (${dataURL.length} chars)`);
      return dataURL;
      
    } catch (error) {
      console.error(`❌ Error resizing image to ${width}x${height}:`, error);
      throw error;
    }
  }

  // Generate all required image sizes from uploaded file (for initial upload)
  async generateImageSizesFromFile(file: File): Promise<ImageSizes> {
    console.log('🎨 Starting image size generation for:', file.name);
    
    try {
      const img = await this.loadImage(file);
      return this.generateImageSizesFromImage(img);
    } catch (error) {
      console.error('❌ Failed to generate image sizes from file:', error);
      throw error;
    }
  }

  // Generate all required image sizes from image URL using cloud function proxy
  async generateImageSizesFromUrl(imageUrl: string, appId: string): Promise<ImageSizes> {
    console.log('🎨 Starting on-demand image size generation via cloud function:', imageUrl);
    
    try {
      const img = await this.loadImageFromUrl(imageUrl, appId);
      return this.generateImageSizesFromImage(img);
    } catch (error) {
      console.error('❌ Failed to generate image sizes via cloud function:', error);
      
      // Try fallback to direct loading
      console.log('🔄 Attempting fallback to direct loading...');
      try {
        const img = await this.loadImageDirectly(imageUrl);
        console.log('✅ Fallback to direct loading succeeded');
        return this.generateImageSizesFromImage(img);
      } catch (fallbackError) {
        console.error('❌ Fallback also failed:', fallbackError);
        throw new Error(`Both cloud function and direct loading failed. Cloud function error: ${error instanceof Error ? error.message : 'Unknown'}. Direct loading error: ${fallbackError instanceof Error ? fallbackError.message : 'CORS blocked'}`);
      }
    }
  }

  // Generate image sizes from loaded image (shared logic)
  private async generateImageSizesFromImage(img: HTMLImageElement): Promise<ImageSizes> {
    console.log('📐 Generating all image sizes...');
    
    try {
      const imageSizes: ImageSizes = {
        favicon16: this.resizeImage(img, 16, 16, 'favicon 16x16'),
        favicon32: this.resizeImage(img, 32, 32, 'favicon 32x32'),
        favicon192: this.resizeImage(img, 192, 192, 'Android chrome 192x192'),
        favicon512: this.resizeImage(img, 512, 512, 'Android chrome 512x512'),
        appleTouchIcon: this.resizeImage(img, 180, 180, 'Apple touch icon'),
        socialMediaImage: this.resizeImage(img, 1200, 630, 'Social media preview')
      };
      
      // Validate all generated images
      console.log('🔍 Validating generated images...');
      const validationResults = Object.entries(imageSizes).map(([key, dataURL]) => {
        const isValid = dataURL && dataURL.startsWith('data:image/') && dataURL.length > 100;
        console.log(`   ${key}: ${isValid ? '✅' : '❌'} (${dataURL?.length || 0} chars)`);
        return { key, isValid, length: dataURL?.length || 0 };
      });
      
      const invalidImages = validationResults.filter(result => !result.isValid);
      if (invalidImages.length > 0) {
        throw new Error(`Failed to generate valid images: ${invalidImages.map(img => img.key).join(', ')}`);
      }
      
      console.log('✅ All image sizes generated successfully');
      
      // Log summary
      const totalSize = validationResults.reduce((sum, result) => sum + result.length, 0);
      console.log(`📊 Image generation summary:
   - Total images: ${validationResults.length}
   - Total data size: ${(totalSize / 1024 / 1024).toFixed(2)} MB
   - All images valid: ✅`);
      
      return imageSizes;
      
    } catch (error) {
      console.error('❌ Failed to generate image sizes:', error);
      throw error;
    }
  }

  // Convert data URL to blob with validation
  dataURLToBlob(dataURL: string): Blob {
    try {
      if (!dataURL || !dataURL.startsWith('data:')) {
        throw new Error('Invalid data URL');
      }
      
      const arr = dataURL.split(',');
      if (arr.length !== 2) {
        throw new Error('Malformed data URL');
      }
      
      const mimeMatch = arr[0].match(/:(.*?);/);
      if (!mimeMatch) {
        throw new Error('No MIME type found in data URL');
      }
      
      const mime = mimeMatch[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      
      return new Blob([u8arr], { type: mime });
    } catch (error) {
      console.error('❌ Error converting data URL to blob:', error);
      throw new Error(`Failed to convert data URL to blob: ${error}`);
    }
  }

  // Generate image files for download with validation
  generateImageFiles(imageSizes: ImageSizes): { name: string; blob: Blob }[] {
    console.log('📦 Generating image file blobs...');
    
    const imageFiles = [
      { name: 'favicon.ico', dataURL: imageSizes.favicon32, description: 'ICO favicon' },
      { name: 'favicon.png', dataURL: imageSizes.favicon32, description: 'PNG favicon' },
      { name: 'favicon-16x16.png', dataURL: imageSizes.favicon16, description: '16x16 favicon' },
      { name: 'favicon-32x32.png', dataURL: imageSizes.favicon32, description: '32x32 favicon' },
      { name: 'android-chrome-192x192.png', dataURL: imageSizes.favicon192, description: 'Android 192x192' },
      { name: 'android-chrome-512x512.png', dataURL: imageSizes.favicon512, description: 'Android 512x512' },
      { name: 'apple-touch-icon.png', dataURL: imageSizes.appleTouchIcon, description: 'Apple touch icon' },
      { name: 'metaimg.png', dataURL: imageSizes.socialMediaImage, description: 'Social media image' }
    ];

    const results: { name: string; blob: Blob }[] = [];
    
    imageFiles.forEach(imageFile => {
      try {
        console.log(`   Processing ${imageFile.name} (${imageFile.description})`);
        const blob = this.dataURLToBlob(imageFile.dataURL);
        results.push({ name: imageFile.name, blob });
        console.log(`   ✅ ${imageFile.name}: ${blob.size} bytes`);
      } catch (error) {
        console.error(`   ❌ Failed to process ${imageFile.name}:`, error);
        // Continue with other files even if one fails
      }
    });
    
    console.log(`📊 Generated ${results.length}/${imageFiles.length} image files successfully`);
    return results;
  }

  // Test if image processing works with cloud function
  async testImageProcessing(imageUrl?: string, appId?: string): Promise<{ success: boolean; method: string; error?: string }> {
    console.log('🧪 Testing image processing capabilities...');
    
    try {
      // Test basic canvas functionality first
      const testCanvas = document.createElement('canvas');
      testCanvas.width = 1;
      testCanvas.height = 1;
      const testCtx = testCanvas.getContext('2d');
      if (!testCtx) {
        throw new Error('Canvas context not available');
      }
      
      testCtx.fillStyle = 'red';
      testCtx.fillRect(0, 0, 1, 1);
      
      const testDataURL = testCanvas.toDataURL('image/png');
      const testBlob = this.dataURLToBlob(testDataURL);
      
      if (testBlob.size === 0) {
        throw new Error('Generated blob is empty');
      }
      
      // If we have an image URL and app ID, test the cloud function
      if (imageUrl && appId && this.functions) {
        try {
          await this.loadImageFromUrl(imageUrl, appId);
          console.log('✅ Cloud function image processing test passed');
          return { success: true, method: 'cloud-function' };
        } catch (cloudError) {
          console.warn('⚠️ Cloud function test failed, trying direct loading:', cloudError);
          
          // Test fallback
          try {
            await this.loadImageDirectly(imageUrl);
            console.log('✅ Direct loading fallback test passed');
            return { success: true, method: 'direct-cors' };
          } catch (directError) {
            console.error('❌ Both cloud function and direct loading failed');
            return { 
              success: false, 
              method: 'none', 
              error: `Cloud function: ${cloudError instanceof Error ? cloudError.message : 'Failed'}. Direct: CORS blocked.`
            };
          }
        }
      }
      
      console.log('✅ Basic image processing test passed');
      return { success: true, method: 'basic-canvas' };
      
    } catch (error) {
      console.error('❌ Image processing test failed:', error);
      return { 
        success: false, 
        method: 'none', 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}

// Factory function to create image processor with Functions
export const createImageProcessor = (functions?: Functions): ImageProcessor => {
  return new ImageProcessor(functions);
};

// Legacy export for backwards compatibility
export const imageProcessor = new ImageProcessor();